<template>
  <base-dialog @cancel="cancelDialog" width="900px">
    <div slot="header">{{ $t('system.service_update_config') }}</div>
    <div slot="body">
      <dialog-selected-tips
        :count="1"
        :name="$t('dictionary.service')"
        :action="$t('system.service_update_config')" />
      <a-spin :spinning="fetchLoading">
        <div
          v-if="!fetchLoading"
          class="service-update-config-cm"
          style="max-height: 520px; overflow-y: auto">
          <code-mirror
            ref="yamlCm"
            v-model="yamlContent"
            :options="cmOptions"
            @input="onYamlEditorInput"
            @ready="onCodeMirrorReady" />
        </div>
      </a-spin>
    </div>
    <div slot="footer">
      <a-button type="primary" :loading="loading" :disabled="fetchLoading" @click="handleConfirm">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/mode/yaml/yaml.js'
import jsYaml from 'js-yaml'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'

export default {
  name: 'ServiceConfigUpdateDialog',
  mixins: [DialogMixin, WindowsMixin],
  data () {
    return {
      loading: false,
      fetchLoading: true,
      yamlContent: '',
      /** 与 code-mirror 每次 @input 同步，提交以此为准，避免清空后仍读到旧 yamlContent */
      editorYamlRaw: '',
      /** 加载完成后的 YAML 文本（trim），用于判断是否与当前一致、无变更则不发请求 */
      baselineYamlRaw: '',
      cmOptions: {
        tabSize: 2,
        styleActiveLine: true,
        lineNumbers: true,
        fixedGutter: true,
        gutters: ['CodeMirror-linenumbers'],
        line: true,
        mode: 'text/x-yaml',
        theme: 'material',
        readOnly: false,
        lineWrapping: true,
      },
    }
  },
  computed: {
    serviceRow () {
      return (this.params.data && this.params.data[0]) || {}
    },
    serviceId () {
      return this.serviceRow.id
    },
  },
  created () {
    this.$eM = new this.$Manager('services', 'v1')
    this.loadConfig()
  },
  methods: {
    onYamlEditorInput (val) {
      this.editorYamlRaw = val == null ? '' : String(val)
    },
    /** 避免在 display:none 内初始化导致行号宽度为 0；挂载后再 refresh 一次 */
    onCodeMirrorReady () {
      this.$nextTick(() => {
        const cm = this.$refs.yamlCm
        if (cm && typeof cm.refresh === 'function') {
          cm.refresh()
        }
      })
    },
    extractConfigDefault (res) {
      const body = res && res.data != null ? res.data : res
      const arr = body && body.data
      if (Array.isArray(arr) && arr[0] && arr[0].config && 'default' in arr[0].config) {
        return arr[0].config.default
      }
      if (body && body.config && 'default' in body.config) {
        return body.config.default
      }
      return undefined
    },
    /** default 无内容时不展示 `{}`，有内容时用块式 YAML（多行 key: value） */
    isEmptyConfigDefault (conf) {
      if (conf == null) return true
      if (typeof conf !== 'object' || Array.isArray(conf)) return false
      return Object.keys(conf).length === 0
    },
    async loadConfig () {
      this.fetchLoading = true
      try {
        const res = await new this.$Manager(`services/${this.serviceId}/config`, 'v1').list()
        const conf = this.extractConfigDefault(res)
        let yaml = ''
        if (!this.isEmptyConfigDefault(conf)) {
          yaml = jsYaml.safeDump(conf, { indent: 2, lineWidth: -1, noRefs: true }) || ''
        }
        this.yamlContent = yaml
        this.editorYamlRaw = this.yamlContent
        this.baselineYamlRaw = this.getSubmitYamlRaw().trim()
      } catch (e) {
        this.yamlContent = ''
        this.editorYamlRaw = ''
        this.baselineYamlRaw = ''
        this.$message.error(this.$t('system.service_config_load_failed'))
      } finally {
        this.fetchLoading = false
      }
    },
    getSubmitYamlRaw () {
      return this.editorYamlRaw == null ? '' : String(this.editorYamlRaw)
    },
    async handleConfirm () {
      if (this.fetchLoading) return
      await this.$nextTick()
      let parsed
      try {
        const raw = this.getSubmitYamlRaw().trim()
        parsed = raw ? jsYaml.safeLoad(raw) : {}
        if (parsed == null) parsed = {}
        if (typeof parsed !== 'object' || Array.isArray(parsed)) {
          this.$message.error(this.$t('system.service_config_yaml_invalid'))
          return
        }
      } catch (e) {
        this.$message.error(this.$t('system.service_config_yaml_invalid'))
        return
      }
      const submitRaw = this.getSubmitYamlRaw().trim()
      if (submitRaw === (this.baselineYamlRaw || '')) {
        this.cancelDialog()
        return
      }
      this.loading = true
      try {
        await this.$eM.performAction({
          id: this.serviceId,
          action: 'config',
          data: {
            config: {
              default: parsed,
            },
          },
        })
        this.$message.success(this.$t('message.exec_success'))
        if (typeof this.params.refresh === 'function') {
          this.params.refresh()
        }
        // 触发侧滑页 singleRefresh，重新拉取详情（含 services/:id/config）
        if (this.$bus && typeof this.$bus.$emit === 'function') {
          this.$bus.$emit('refresh-detail')
        }
        this.cancelDialog()
      } catch (error) {
        throw error
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* 无内容时仍保留约 5 行编辑区域（避免整段缩成一行高） */
.service-update-config-cm ::v-deep .CodeMirror {
  min-height: 130px;
}
.service-update-config-cm ::v-deep .CodeMirror-scroll {
  min-height: 130px;
}
.service-update-config-cm ::v-deep .CodeMirror-gutters {
  min-width: 3em;
}
.service-update-config-cm ::v-deep .CodeMirror-linenumber {
  min-width: 2.25em;
  text-align: right;
}
</style>
